import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const safetyPanel = "rounded-lg border border-zinc-200 bg-white dark:border-white/10 dark:bg-[#101620]";
export const safetyButton = "inline-flex min-h-12 items-center justify-center rounded-md px-4 font-bold text-sm border border-zinc-300 dark:border-white/20 hover:border-[#FF5F15]";
export const safetyMuted = "text-zinc-500 dark:text-zinc-400";
export const statusText = action => action.status === "verified" ? "Verified" : action.status === "completed" ? "Completed · awaiting verification" : action.further_action_required ? "Further action required" : ({ overdue: "Overdue", due_today: "Due today", upcoming: "Upcoming", no_due_date: "No due date" }[action.urgency] || action.status.replaceAll("_", " "));

export function useMySafetyRequest(path, params) {
  const { user } = useAuth();
  const identity = `${user?.org_id || ""}:${user?.id || ""}`;
  const [snapshot, setSnapshot] = useState(null);
  const data = snapshot?.identity === identity ? snapshot.data : null;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const query = JSON.stringify(params || {});
  useEffect(() => {
    const refresh = () => { setOnline(navigator.onLine); setVersion(v => v + 1); };
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", refresh);
    return () => { window.removeEventListener("focus", refresh); window.removeEventListener("online", refresh); window.removeEventListener("offline", refresh); };
  }, []);
  useEffect(() => {
    let live = true;
    setSnapshot(null); setError(""); setLoading(true);
    if (!navigator.onLine) { setLoading(false); setError("Connect to the internet to view or complete safety actions."); return; }
    api.get(path, { params: JSON.parse(query), __flOnlineOnly: true }).then(r => { if (live) setSnapshot({ identity, data: r.data }); })
      .catch(e => { if (live) setError([403, 404].includes(e.response?.status) ? "This assigned action is no longer available to you." : "Could not load safety actions. Check your connection and retry."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [path, query, version, identity]);
  return { data, error, loading, online, refresh: () => setVersion(v => v + 1) };
}

export function MySafetyCard({ action }) {
  return <article className={`${safetyPanel} p-4 space-y-2`}>
    <div className={`text-xs uppercase font-bold ${action.urgency === "overdue" && action.status !== "completed" && action.status !== "verified" ? "text-red-700 dark:text-red-300" : "text-[#FF5F15]"}`}>{statusText(action)}</div>
    <h3 className="font-display text-lg font-bold break-words">{action.title}</h3>
    {action.job && <p className="text-sm">{action.job.number} {action.job.title}</p>}
    <p className={`text-xs ${safetyMuted}`}>{action.due_date ? `Due ${action.due_date}` : "No due date"} · From: {action.source_type?.replaceAll("_", " ") || "Safety record"}</p>
    <Link to={`/app/my-safety/${encodeURIComponent(action.id)}`} className={`${safetyButton} w-full text-[#FF5F15]`}>Open action</Link>
  </article>;
}

export default function MySafetyHomeCard() {
  const { data, error, loading, refresh } = useMySafetyRequest("/my/safety-actions", { limit: 3 });
  if (loading || (!data && !error) || (data && data.counts.open === 0)) return null;
  return <section className={`${safetyPanel} p-4 mb-4 text-zinc-900 dark:text-zinc-100`} data-testid="field-safety-actions">
    <header className="flex justify-between items-center gap-2"><h2 className="font-display font-bold uppercase">Safety actions</h2><Link className={`${safetyButton} text-[#FF5F15]`} to="/app/my-safety">View all</Link></header>
    {error ? <div role="alert" className="text-sm py-2">{error}<button className={safetyButton} onClick={refresh}>Retry</button></div> : <>
      <p className={`text-xs my-3 ${safetyMuted}`}>{data.counts.open} open · {data.counts.overdue} overdue · {data.counts.due_today} due today</p>
      <ul className="divide-y divide-zinc-200 dark:divide-white/10">{data.items.map(action => <li key={action.id}><Link className="block py-3 min-h-14" to={`/app/my-safety/${encodeURIComponent(action.id)}`}><span className="text-[10px] font-bold uppercase text-[#FF5F15]">{statusText(action)}</span><strong className="block text-sm">{action.title}</strong>{action.job && <span className={`text-xs ${safetyMuted}`}>{action.job.number} {action.job.title}</span>}</Link></li>)}</ul>
    </>}
  </section>;
}
